import { None } from "./types";
import { Scalar, ScalarProvider } from "./Scalar";
import { Stategroup, StateSwitch } from "./StateSwitch";
import { Pipe } from "./Pipe";
import { And } from "./And";
import { Or } from "./Or";
import { Fallback } from "./Fallback";
import { DictionaryProvider } from "./Dictionary";
import { DictionaryMap, DynamicDictionaryMap } from "./Map";
import { GroupBy, DynamicGroupBy } from "./GroupBy";
import { Filter, DynamicFilter } from "./Filter";
import { Get } from "./Get";
import { Reduce } from "./Reduce";
import { GetSingleton } from "./GetSingleton";

const locationPattern = /:(\d+):(\d+)/;

export function getSourceOriginFromStacktrace(error: Error, line: number): string {
	const stack = error.stack?.split("\n")[line - 1]?.replace(/http.*(?:engine)/i, "engine");
	if (stack === undefined) return "<failed>";
	const location_parts = locationPattern.exec(stack)!;
	return (global as any)?.sourceMapper(+location_parts[1], +location_parts[2]);
}

type ExcludeNone<Type> = Type extends ScalarProvider<infer T> ? ScalarProvider<Exclude<T, None>> : Exclude<Type, None>;
type Static<Type> = Type extends Scalar<infer T> ? T : Type;

export class ExpressionBuilder<ArgumentsType, ResultType = ArgumentsType> {
	private readonly steps: Array<(arg: unknown) => unknown> = [];

	constructor(input?: Scalar<ArgumentsType>) {
		const builder: any = function (args?: Scalar<ArgumentsType>): Scalar<ArgumentsType> {
			let result: any = args ?? input!;
			for (const step of builder.steps) {
				result = step(result);
			}
			return result;
		};
		Object.assign(builder, this);
		Object.setPrototypeOf(builder, Object.getPrototypeOf(this));
		return builder;
	}

	stateSwitch<InType extends Stategroup, SwitchResultType>(
		states: {
			[key in InType["tag"]]: ($: Extract<InType, { tag: key }>["value"]) => Scalar<SwitchResultType>;
		}
	) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<InType>) => {
			if (source instanceof ScalarProvider) {
				return new StateSwitch(source, states, origin);
			} else {
				return states[source.tag as InType["tag"]](source.value);
			}
		});
		return this;
	}

	map<KeyType, ValueInType, ValueOutType>(map_fn: ($: [KeyType, ValueInType]) => ValueOutType) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<DictionaryProvider<KeyType, ValueInType>>) => {
			if (source instanceof ScalarProvider) {
				return new DynamicDictionaryMap(source, map_fn, origin);
			} else {
				return new DictionaryMap(source, map_fn, origin);
			}
		});
		return this;
	}

	filter() {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<DictionaryProvider<KeyType, ResultType>>) => {
			if (source instanceof ScalarProvider) {
				return new DynamicFilter(source, origin);
			} else {
				return new Filter(source, origin);
			}
		});
		return this;
	}

	groupBy<KeyType, ValueType, GroupType>(group_fn: ($: [KeyType, ValueType]) => Scalar<GroupType>) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<DictionaryProvider<KeyType, ValueType>>) => {
			if (source instanceof ScalarProvider) {
				return new DynamicGroupBy(source, group_fn, origin);
			} else {
				return new GroupBy(source, group_fn, origin);
			}
		});
		return this;
	}

	get<KeyType, ValueType>(key_expr?: Scalar<KeyType>) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		if (key_expr === undefined) {
			this.steps.push((source: Scalar<DictionaryProvider<KeyType, ValueType>>) => new GetSingleton(source, origin));
		} else {
			this.steps.push((source: Scalar<DictionaryProvider<KeyType, ValueType>>) => new Get(source, key_expr, origin));
		}
		return this;
	}

	reduce<KeyType, ValueType>(fn: (accumulator: ResultType, value: ValueType) => ResultType, initial_value: ResultType) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push(
			(source: Scalar<DictionaryProvider<KeyType, ValueType>>) => new Reduce(source, fn, initial_value, origin)
		);
		return this;
	}

	pipe<PipeResultType>(fn: ($: ResultType) => Scalar<PipeResultType>, extra_steps_to_origin = 0) {
		if (fn instanceof ExpressionBuilder) {
			for (const step of fn.steps) {
				this.steps.push(step);
			}
			return this;
		}
		const origin = (<any>global)?.alan_debug
			? getSourceOriginFromStacktrace(new Error(), 2 + extra_steps_to_origin)
			: undefined;
		this.steps.push((source: Scalar<ResultType>) => {
			if (source instanceof ScalarProvider) {
				return new Pipe(source, fn, origin);
			} else {
				return fn(source);
			}
		});
		return this;
	}

	and<RightType>(right: Scalar<RightType>) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<ResultType>) => {
			if (source instanceof ScalarProvider || right instanceof ScalarProvider) {
				return new And(source, right, origin);
			} else {
				return [source, right];
			}
		});
		return this;
	}

	or<RightType>(right: Scalar<RightType>) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<ResultType>) => {
			if (source instanceof ScalarProvider) {
				return new Or(source, right, origin);
			} else {
				return (source as any) === None ? right : source;
			}
		});
		return this;
	}

	fallback(fallback_value: ResultType) {
		const origin = (<any>global)?.alan_debug ? getSourceOriginFromStacktrace(new Error(), 2) : undefined;
		this.steps.push((source: Scalar<ResultType>) => {
			if (source instanceof ScalarProvider) {
				return new Fallback(source, fallback_value, origin);
			} else {
				return source;
			}
		});
		return this;
	}
}

/*
 *  Uninitialized undetermined variant
 */

export interface StategroupExpressionBuilder<ArgumentsType, ResultType extends Stategroup>
	extends DefaultExpressionBuilder<ArgumentsType, ResultType> {
	stateSwitch<SwitchResultType>(
		states: {
			[key in ResultType["tag"]]: ($: Extract<ResultType, { tag: key }>["value"]) => Scalar<SwitchResultType>;
		}
	): DefaultExpressionBuilder<ArgumentsType, SwitchResultType>;
}

export interface DictionaryExpressionBuilder<ArgumentsType, KeyType, ValueType>
	extends DefaultExpressionBuilder<ArgumentsType, DictionaryProvider<KeyType, Scalar<ValueType>>> {
	map<MapResultType>(
		mapping: ($: [KeyType, ValueType]) => Scalar<MapResultType>
	): DictionaryExpressionBuilder<ArgumentsType, KeyType, MapResultType>;
	filter(): DictionaryExpressionBuilder<ArgumentsType, KeyType, ExcludeNone<ValueType>>;
	groupBy<GroupValueType>(
		value_function: ($: [KeyType, ValueType]) => Scalar<GroupValueType>
	): DictionaryExpressionBuilder<ArgumentsType, GroupValueType, DictionaryProvider<KeyType, ValueType>>;
	get(key_expr?: Scalar<KeyType>): DefaultExpressionBuilder<ArgumentsType, ValueType | None>;
	reduce<ResultType>(
		fn: (accumulator: ResultType, value: ValueType) => ResultType,
		initial_value: ResultType
	): DefaultExpressionBuilder<ArgumentsType, ResultType>;
}

export interface DefaultExpressionBuilder<ArgumentsType, ResultType> {
	and<RightType>(right: Scalar<RightType>): DefaultExpressionBuilder<ArgumentsType, [ResultType, RightType]>;
	or<RightType>(
		right: Scalar<RightType>
	): DefaultExpressionBuilder<ArgumentsType, Exclude<ResultType, None> | RightType>;
	pipe<PipeResultType>(
		fn: ($: ResultType) => Scalar<PipeResultType>,
		extra_steps_to_origin?: number
	): DefaultExpressionBuilder<ArgumentsType, PipeResultType>;
	fallback(fallback_value: ResultType): DefaultExpressionBuilder<ArgumentsType, ResultType>;
	(args: Scalar<ArgumentsType>): Scalar<ResultType>;
}

/*
 *  Initialized static variant
 */

export interface InitializedStaticStategroupExpressionBuilder<ArgumentsType, ResultType extends Stategroup>
	extends InitializedStaticDefaultExpressionBuilder<ArgumentsType, ResultType> {
	stateSwitch<SwitchResultType>(
		states: {
			[key in ResultType["tag"]]: ($: Extract<ResultType, { tag: key }>["value"]) => Scalar<SwitchResultType>;
		}
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, SwitchResultType>;
}

export interface InitializedStaticDictionaryExpressionBuilder<ArgumentsType, KeyType, ValueType>
	extends InitializedStaticDefaultExpressionBuilder<ArgumentsType, DictionaryProvider<KeyType, Scalar<ValueType>>> {
	map<MapResultType>(
		mapping: ($: [KeyType, ValueType]) => Scalar<MapResultType>
	): InitializedStaticDictionaryExpressionBuilder<ArgumentsType, KeyType, MapResultType>;
	filter(): InitializedStaticDictionaryExpressionBuilder<ArgumentsType, KeyType, ExcludeNone<ValueType>>;
	groupBy<GroupValueType>(
		value_function: ($: [KeyType, ValueType]) => Scalar<GroupValueType>
	): InitializedStaticDictionaryExpressionBuilder<
		ArgumentsType,
		GroupValueType,
		DictionaryProvider<KeyType, ValueType>
	>;
	get(key_expr?: Scalar<KeyType>): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ValueType | None>;
	reduce<ResultType>(
		fn: (accumulator: ResultType, value: ValueType) => ResultType,
		initial_value: ResultType
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ResultType>;
}

export interface InitializedStaticDefaultExpressionBuilder<ArgumentsType, ResultType> {
	and<RightType>(
		right: ScalarProvider<RightType>
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, [ResultType, RightType]>;
	and<RightType>(right: RightType): InitializedStaticDefaultExpressionBuilder<ArgumentsType, [ResultType, RightType]>;
	and<RightType>(
		right: Scalar<RightType>
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, [ResultType, RightType]>;
	or<RightType>(
		right: Scalar<RightType>
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, Exclude<ResultType, None> | RightType>;
	pipe<PipeResultType>(
		fn: ($: ResultType) => ScalarProvider<PipeResultType>,
		extra_steps_to_origin?: number
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, PipeResultType>;
	pipe<PipeResultType>(
		fn: ($: ResultType) => Scalar<PipeResultType>,
		extra_steps_to_origin?: number
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, PipeResultType>;
	pipe<PipeResultType>(
		fn: ($: ResultType) => PipeResultType,
		extra_steps_to_origin?: number
	): InitializedStaticDefaultExpressionBuilder<ArgumentsType, PipeResultType>;
	pipe<PipeResultType>(
		fn: ($: ResultType) => Scalar<PipeResultType>,
		extra_steps_to_origin?: number
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, PipeResultType>;
	fallback(fallback_value: ResultType): InitializedStaticDefaultExpressionBuilder<ArgumentsType, ResultType>;
	(): ResultType;
}

/*
 *  Initialized dynamic variant
 */

export interface InitializedDynamicStategroupExpressionBuilder<ArgumentsType, ResultType extends Stategroup>
	extends InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ResultType> {
	stateSwitch<SwitchResultType>(
		states: {
			[key in ResultType["tag"]]: ($: Extract<ResultType, { tag: key }>["value"]) => Scalar<SwitchResultType>;
		}
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, SwitchResultType>;
}

export interface InitializedDynamicDictionaryExpressionBuilder<ArgumentsType, KeyType, ValueType>
	extends InitializedDynamicDefaultExpressionBuilder<ArgumentsType, DictionaryProvider<KeyType, Scalar<ValueType>>> {
	map<MapResultType>(
		mapping: ($: [KeyType, ValueType]) => Scalar<MapResultType>
	): InitializedDynamicDictionaryExpressionBuilder<ArgumentsType, KeyType, MapResultType>;
	filter(): InitializedDynamicDictionaryExpressionBuilder<ArgumentsType, KeyType, ExcludeNone<ValueType>>;
	groupBy<GroupValueType>(
		value_function: ($: [KeyType, ValueType]) => Scalar<GroupValueType>
	): InitializedDynamicDictionaryExpressionBuilder<
		ArgumentsType,
		GroupValueType,
		DictionaryProvider<KeyType, ValueType>
	>;
	get(key_expr?: Scalar<KeyType>): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ValueType | None>;
	reduce<ResultType>(
		fn: (accumulator: ResultType, value: ValueType) => ResultType,
		initial_value: ResultType
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ResultType>;
}

export interface InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ResultType> {
	and<RightType>(
		right: Scalar<RightType>
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, [ResultType, RightType]>;
	or<RightType>(
		right: Scalar<RightType>
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, Exclude<ResultType, None> | RightType>;
	pipe<PipeResultType>(
		fn: ($: Static<ResultType>) => Scalar<PipeResultType>,
		extra_steps_to_origin?: number
	): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, PipeResultType>;
	fallback(fallback_value: ResultType): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ResultType>;
	(): ScalarProvider<ResultType>;
}

/*
 *  Caller
 */

export function createStategroupExpressionBuilder<ArgumentsType extends Stategroup>(
	input: ScalarProvider<ArgumentsType>
): InitializedDynamicStategroupExpressionBuilder<ArgumentsType, ArgumentsType>;
export function createStategroupExpressionBuilder<ArgumentsType extends Stategroup>(
	input: ArgumentsType
): InitializedStaticStategroupExpressionBuilder<ArgumentsType, ArgumentsType>;
export function createStategroupExpressionBuilder<ArgumentsType extends Stategroup>(): StategroupExpressionBuilder<
	ArgumentsType,
	ArgumentsType
>;
export function createStategroupExpressionBuilder<ArgumentsType>(
	input?: Scalar<ArgumentsType>
): (args?: Scalar<ArgumentsType>) => Scalar<ArgumentsType> {
	return new ExpressionBuilder(input) as any;
}

export function createDictionaryExpressionBuilder<KeyType, ValueType>(
	input: ScalarProvider<DictionaryProvider<KeyType, Scalar<ValueType>>>
): InitializedDynamicDictionaryExpressionBuilder<DictionaryProvider<KeyType, Scalar<ValueType>>, KeyType, ValueType>;
export function createDictionaryExpressionBuilder<KeyType, ValueType>(
	input: DictionaryProvider<KeyType, Scalar<ValueType>>
): InitializedStaticDictionaryExpressionBuilder<DictionaryProvider<KeyType, Scalar<ValueType>>, KeyType, ValueType>;
export function createDictionaryExpressionBuilder<KeyType, ValueType>(): DictionaryExpressionBuilder<
	DictionaryProvider<KeyType, Scalar<ValueType>>,
	KeyType,
	ValueType
>;
export function createDictionaryExpressionBuilder<ArgumentsType>(
	input?: Scalar<ArgumentsType>
): (args?: Scalar<ArgumentsType>) => Scalar<ArgumentsType> {
	return new ExpressionBuilder(input) as any;
}

export function createExpressionBuilder<ArgumentsType>(
	input: ScalarProvider<ArgumentsType>
): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ArgumentsType>;
export function createExpressionBuilder<ArgumentsType>(
	input: Scalar<ArgumentsType>
): InitializedDynamicDefaultExpressionBuilder<ArgumentsType, ArgumentsType>;
export function createExpressionBuilder<ArgumentsType>(
	input: ArgumentsType
): InitializedStaticDefaultExpressionBuilder<ArgumentsType, ArgumentsType>;
export function createExpressionBuilder<ArgumentsType>(): DefaultExpressionBuilder<ArgumentsType, ArgumentsType>;
export function createExpressionBuilder<ArgumentsType>(
	input?: Scalar<ArgumentsType>
): (args?: Scalar<ArgumentsType>) => Scalar<ArgumentsType> {
	return new ExpressionBuilder(input) as any;
}

export function lambda<ArgumentsType, ResultType>(fn: ($: ArgumentsType) => Scalar<ResultType>) {
	return createExpressionBuilder<ArgumentsType>().pipe(fn);
}
